import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import getRelativeLuminance from 'get-relative-luminance';
import R from 'ramda';

const MINIMUM_LUMINANCE = 0.9;

const getOnChange = ({
  setProductSKU,
  onChange,
  sku,
  shade,
}) => () => {
  setProductSKU(sku);
  if (onChange) {
    onChange({
      shade,
      sku,
    });
  }
};

const generateLabelClassNames = ({
  skuImage,
  skuColor,
  availableQuantity,
  enableQuantityCheck,
}) => classNames(
  'product-radio__label',
  {
    // ! TODO: [2] create working README.md  example for this
    'product-radio__label--image': skuImage,
    // Show a border when the call to getRelativeLuminance throws an error (e.g. when skuColor is undefined)
    'product-radio__label--border': R.tryCatch(() => getRelativeLuminance(skuColor) > MINIMUM_LUMINANCE, R.T),
    'product-radio__label--out-of-stock': !!(enableQuantityCheck && availableQuantity === 0),
  },
);

const generateInputClassNames = ({
  checked,
}) => classNames(
  'product-radio__input',
  {
    'product-radio__input-checked': checked,
  },
);

const ProductSwatchColoursItemMarkup = forwardRef(({
  uniqueId = '',
  swatches,
  selectedSKU,
  setProductSKU,
  onChange,
  enableQuantityCheck = false,
  testAttribute = 'product-swatch-colours-item',
}, ref) => (
  Array.isArray(swatches) && (
    <ul className="product-radio" ref={ref}>
      {swatches.map(({
        sku,
        skuColor,
        skuImage,
        itemName: shade,
        availableQuantity,
        customName,
      }, index) => (
        <li className="product-radio__input-list-item" key={sku}>
          <input
            aria-label={`${sku}${uniqueId}${index}`}
            type="radio"
            id={`${sku}${uniqueId}${index}`}
            name="product-radio-selector"
            className={generateInputClassNames({ checked: sku === selectedSKU })}
            onChange={getOnChange({
              setProductSKU,
              onChange,
              sku,
              shade,
            })}
          />
          <label
            data-test={testAttribute}
            aria-labelledby={`${sku}${uniqueId}${index}`}
            htmlFor={`${sku}${uniqueId}${index}`}
            className={generateLabelClassNames({
              skuImage,
              skuColor,
              availableQuantity,
              enableQuantityCheck,
            })}
            style={{
              ...(!enableQuantityCheck || availableQuantity > 0) && { backgroundColor: skuColor },
              ...(
                (!enableQuantityCheck || availableQuantity > 0) &&
                skuImage && { backgroundImage: `url(${skuImage})` }
                ),
            }}
          >
            {enableQuantityCheck && availableQuantity === 0 && (
              <div className="product-radio__out-of-stock-cross" />
            )}
          </label>

          {customName && <p className="product-radio__swatch-custom-name">{customName}</p>}
        </li>
      ))}
    </ul>
  )
));

ProductSwatchColoursItemMarkup.propTypes = {
  swatches: PropTypes.arrayOf(PropTypes.shape({
    // TODO: [2] define proper shape structure for swatch object
    sku: PropTypes.string.isRequired,
    skuColor: PropTypes.string,
  })).isRequired,
  setProductSKU: PropTypes.func,
  selectedSKU: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  testAttribute: PropTypes.string,
  enableQuantityCheck: PropTypes.bool,
};

export default ProductSwatchColoursItemMarkup;
